import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, CheckCircle2, Clock } from 'lucide-react';

const BundeslaenderSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const bundeslaender = [
    { name: 'Rheinland-Pfalz', status: 'live', contacts: '50.000+', description: 'Alle 202 Gemeinden erfasst' },
    { name: 'Nordrhein-Westfalen', status: 'coming', contacts: '200.000+', description: 'Bald verfügbar – NRWs vollständige Abdeckung' },
    { name: 'Bayern', status: 'planned', contacts: '150.000+', description: 'In Planung' },
    { name: 'Baden-Württemberg', status: 'planned', contacts: '130.000+', description: 'In Planung' },
    { name: 'Hessen', status: 'planned', contacts: '80.000+', description: 'In Planung' },
    { name: 'Niedersachsen', status: 'planned', contacts: '90.000+', description: 'In Planung' },
  ];

  const getStatusBadge = (status) => {
    if (status === 'live') {
      return (
        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
          <CheckCircle2 className="w-4 h-4" />
          Verfügbar
        </span>
      );
    } else if (status === 'coming') {
      return (
        <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
          <Clock className="w-4 h-4" />
          Bald verfügbar
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
          <Clock className="w-4 h-4" />
          In Planung
        </span>
      );
    }
  };

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Bundesland für Bundesland
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Wir starten mit Rheinland-Pfalz und bauen systematisch aus. 
            Unser Ziel: Alle 16 Bundesländer mit 100% Abdeckung – 
            von Großstädten bis zu kleinen Dörfern.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bundeslaender.map((land, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className={`relative rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 ${
                land.status === 'live' 
                  ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-300' 
                  : land.status === 'coming'
                  ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300'
                  : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className={`w-6 h-6 ${
                    land.status === 'live' ? 'text-green-600' : 
                    land.status === 'coming' ? 'text-blue-600' : 'text-gray-600'
                  }`} />
                  <h3 className="text-xl font-bold text-gray-900">{land.name}</h3>
                </div>
                {getStatusBadge(land.status)}
              </div>
              
              <div className="mb-3">
                <div className="text-3xl font-bold text-[#2835F7]">{land.contacts}</div>
                <div className="text-sm text-gray-600">Geschätzte Kontakte</div>
              </div>
              
              <p className="text-gray-700 text-sm">{land.description}</p>
              
              {land.status === 'live' && (
                <div className="absolute -top-2 -right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                  JETZT NUTZEN!
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 bg-gradient-to-br from-[#2835F7] to-[#1a25c9] rounded-2xl p-8 text-center text-white shadow-xl"
        >
          <h3 className="text-2xl font-bold mb-4">
            Unser Versprechen: 100% Abdeckung
          </h3>
          <p className="text-lg text-white/90 max-w-3xl mx-auto">
            Andere Plattformen listen nur die Großstädte. Wir gehen weiter: 
            Jede Gemeinde, jeder Landkreis, jedes Dorf – lückenlos erfasst. 
            So verpassen Sie keine Geschäftschance mehr.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default BundeslaenderSection;